import { SystemCategoryCode } from '@bcube/shared';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Info } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Link } from 'react-router';
import { BRAZILIAN_UF_LIST, TO_BILL_FOR_OPTIONS } from '@/api/commercial/price-tables';
import { productApi } from '@/api/commercial/products';
import {
  CancelPriceTableDialog,
  InactivatePriceTableDialog,
  PublishPriceTableDialog,
} from '@/components/dialogs/commercial/price-table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { usePriceTableForm } from '@/hooks/commercial/use-price-table-form';
import { BillingConditionsSection } from './billing-conditions-section';
import { DateTimeInput } from './date-time-input';
import { EquipmentPriceForm } from './equipment-price-form';
import { ServicePriceForm } from './service-price-form';
import { SimCardPriceForm } from './simcard-price-form';
import { StatusBanner } from './status-banner';

interface UpsertPriceTableFormProps {
  priceTableId?: string;
  editMode?: boolean;
  cloneMode?: boolean;
}

export function UpsertPriceTableForm({
  priceTableId,
  editMode = false,
  cloneMode = false,
}: UpsertPriceTableFormProps = {}) {
  const queryClient = useQueryClient();

  // Fetch equipment products (RASTREADOR)
  const { data: equipmentData, isLoading: loadingEquipment } = useQuery({
    queryKey: ['products', 'equipment', SystemCategoryCode.BWS_TRACKER],
    queryFn: () =>
      productApi.findMany({
        filter: { categoryCode: SystemCategoryCode.BWS_TRACKER },
        limit: 1000,
      }),
  });

  // Fetch accessories products (ACESSORIO)
  const { data: accessoriesData, isLoading: loadingAccessories } = useQuery({
    queryKey: ['products', 'accessories', SystemCategoryCode.BWS_ACCESSORY],
    queryFn: () =>
      productApi.findMany({
        filter: { categoryCode: SystemCategoryCode.BWS_ACCESSORY },
        limit: 1000,
      }),
  });

  const equipmentModels = useMemo(() => equipmentData?.docs || [], [equipmentData?.docs]);
  const accessoriesItems = useMemo(() => accessoriesData?.docs || [], [accessoriesData?.docs]);
  const loadingProducts = loadingEquipment || loadingAccessories;

  const {
    form,
    handleSubmit,
    loading,
    handleEquipmentPriceChange,
    handleSimCardPriceChange,
    handleAccessoryPriceChange,
    handleServicePriceChange,
    handleValidationConditions,
    messageErrorCondition,
    STATUS_STYLES,
    existingEquipmentPayment,
    existingEquipmentSimcardPayment,
    existingSimcardPayment,
    existingServicePayment,
    priceTableStatus,
    priceTableName,
    priceTableIdentifier,
  } = usePriceTableForm({ priceTableId, editMode, cloneMode });

  const [enabledEquipmentWithSim, setEnabledEquipmentWithSim] = useState<Record<string, boolean>>(
    {},
  );
  const [enabledEquipmentWithoutSim, setEnabledEquipmentWithoutSim] = useState<
    Record<string, boolean>
  >({});
  const [enabledAccessories, setEnabledAccessories] = useState<Record<string, boolean>>({});
  const [isFormDisabled, setIsFormDisabled] = useState(false);

  // Dialog states
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [inactivateDialogOpen, setInactivateDialogOpen] = useState(false);

  const isInitialized = useRef(false);
  const [disclosureDefaultOpen, setDisclosureDefaultOpen] = useState(() => ({
    priceWithSim: false,
    priceWithoutSim: false,
    accessories: false,
    simCard: false,
    services: false,
  }));

  const handleEquipmentToggle = (
    equipment: string,
    enabled: boolean,
    type: 'withSim' | 'withoutSim',
  ) => {
    if (type === 'withSim') {
      setEnabledEquipmentWithSim((prev) => ({
        ...prev,
        [equipment]: enabled,
      }));
    } else {
      setEnabledEquipmentWithoutSim((prev) => ({
        ...prev,
        [equipment]: enabled,
      }));
    }
  };

  const handleAccessoryToggle = (accessory: string, enabled: boolean) => {
    setEnabledAccessories((prev) => ({
      ...prev,
      [accessory]: enabled,
    }));
  };

  useEffect(() => {
    if (isInitialized.current) return;

    const hasAnyData =
      existingEquipmentSimcardPayment.length > 0 || existingEquipmentPayment.length > 0;
    if (!hasAnyData) return;

    const newEnabledEquipmentWithSim: Record<string, boolean> = {};
    const newEnabledEquipmentWithoutSim: Record<string, boolean> = {};
    const newEnabledAccessories: Record<string, boolean> = {};
    const accessoryIds = new Set(accessoriesItems.map((acc) => acc.id));

    for (const equipment of existingEquipmentSimcardPayment) {
      newEnabledEquipmentWithSim[equipment.productId] = true;
    }

    for (const equipment of existingEquipmentPayment) {
      if (accessoryIds.has(equipment.productId)) {
        newEnabledAccessories[equipment.productId] = true;
      } else {
        newEnabledEquipmentWithoutSim[equipment.productId] = true;
      }
    }

    setEnabledEquipmentWithSim(newEnabledEquipmentWithSim);
    setEnabledEquipmentWithoutSim(newEnabledEquipmentWithoutSim);
    setEnabledAccessories(newEnabledAccessories);

    isInitialized.current = true;
  }, [existingEquipmentPayment, existingEquipmentSimcardPayment, accessoriesItems]);

  const hasEquipmentWithSimData = useMemo(
    () => Object.values(enabledEquipmentWithSim).some(Boolean),
    [enabledEquipmentWithSim],
  );
  const hasEquipmentWithoutSimData = useMemo(
    () => Object.values(enabledEquipmentWithoutSim).some(Boolean),
    [enabledEquipmentWithoutSim],
  );
  const hasAccessoryData = useMemo(
    () => Object.values(enabledAccessories).some(Boolean),
    [enabledAccessories],
  );
  const hasSimCardData = useMemo(
    () => Boolean(existingSimcardPayment && existingSimcardPayment.length > 0),
    [existingSimcardPayment],
  );
  const hasServiceData = useMemo(
    () => Boolean(existingServicePayment && existingServicePayment.length > 0),
    [existingServicePayment],
  );

  useEffect(() => {
    setDisclosureDefaultOpen((prev) => ({
      priceWithSim: prev.priceWithSim || hasEquipmentWithSimData,
      priceWithoutSim: prev.priceWithoutSim || hasEquipmentWithoutSimData,
      accessories: prev.accessories || hasAccessoryData,
      simCard: prev.simCard || hasSimCardData,
      services: prev.services || hasServiceData,
    }));
  }, [
    hasEquipmentWithSimData,
    hasEquipmentWithoutSimData,
    hasAccessoryData,
    hasSimCardData,
    hasServiceData,
  ]);

  useEffect(() => {
    const blockedStatuses = ['ACTIVE', 'INACTIVE', 'CANCELLED'];
    setIsFormDisabled(blockedStatuses.includes(priceTableStatus ?? '') && !cloneMode);
  }, [priceTableStatus, cloneMode]);

  const hasRequiredPaymentData = () => {
    const hasExistingPayments =
      (existingEquipmentPayment && existingEquipmentPayment.length > 0) ||
      (existingEquipmentSimcardPayment && existingEquipmentSimcardPayment.length > 0) ||
      (existingSimcardPayment && existingSimcardPayment.length > 0) ||
      (existingServicePayment && existingServicePayment.length > 0);

    return hasExistingPayments;
  };

  const getStatusBadge = (status: string | null) => {
    if (!status) return null;

    const statusColors: Record<string, string> = {
      DRAFT: 'bg-gray-100 text-gray-800',
      ACTIVE: 'bg-green-100 text-green-800',
      INACTIVE: 'bg-red-100 text-red-800',
      CANCELLED: 'bg-red-100 text-red-800',
      AWAITING_PUBLICATION: 'bg-yellow-100 text-yellow-800',
    };

    const statusLabels: Record<string, string> = {
      DRAFT: 'Rascunho',
      ACTIVE: 'Ativo',
      INACTIVE: 'Inativo',
      CANCELLED: 'Cancelada',
      AWAITING_PUBLICATION: 'Aguardando Publicação',
    };

    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-800';
    const label = statusLabels[status] || status;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
      >
        {label}
      </span>
    );
  };

  const isCreatingNew = !editMode || !priceTableId;
  const isDraft = priceTableStatus === 'DRAFT';
  const isAwaitingPublication = priceTableStatus === 'AWAITING_PUBLICATION';
  const isActive = priceTableStatus === 'ACTIVE';
  const isPaused = priceTableStatus === 'PAUSED';

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-gray-900">
            Tabela de Preços
            {editMode && priceTableName && (
              <span className="text-gray-500 font-normal"> — {priceTableName}</span>
            )}
          </h1>
          {editMode && priceTableId && getStatusBadge(priceTableStatus)}
        </div>
        <div className="flex gap-2">
          {isDraft && hasRequiredPaymentData() && (
            <Button
              type="button"
              className="bg-black hover:bg-gray-800 text-white"
              onClick={() => setPublishDialogOpen(true)}
            >
              Publicar
            </Button>
          )}
          <Link to="/commercial/price-table">
            <Button type="button" variant="outline">
              Voltar
            </Button>
          </Link>
          {(isAwaitingPublication || isDraft) && editMode && priceTableId && (
            <Button
              type="button"
              variant="outline"
              className="text-red-600 border-red-300 hover:bg-red-50"
              onClick={() => setCancelDialogOpen(true)}
            >
              Cancelar tabela
            </Button>
          )}
          {(isActive || isPaused) && priceTableId && (
            <Button type="button" variant="outline" onClick={() => setInactivateDialogOpen(true)}>
              Inativar tabela
            </Button>
          )}
          {(isCreatingNew || isDraft || isAwaitingPublication) && (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="bg-black hover:bg-gray-800 text-white"
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          )}
        </div>
      </div>

      {editMode && priceTableId && (
        <p className="text-xs text-gray-400">ID: {priceTableIdentifier ?? priceTableId}</p>
      )}

      <FormProvider {...form}>
        <form id="price-table-form" onSubmit={(e) => e.preventDefault()} className="space-y-3">
          {/* General Settings */}
          <div>
            <div className="border-b border-gray-200 w-full py-2">
              <p className="font-semibold text-gray-800 text-base">Configurações Gerais</p>
            </div>
            <div className="pt-3 space-y-3">
              {/* Name */}
              <div className="space-y-1">
                <div className="text-sm font-medium text-gray-900">
                  Nome da tabela <span className="text-red-500">*</span>
                </div>
                <Input
                  {...form.register('name')}
                  disabled={isFormDisabled}
                  placeholder="Nome da tabela"
                />
                {form.formState.errors.name?.message && (
                  <p className="text-sm text-red-600">{form.formState.errors.name?.message}</p>
                )}
              </div>

              {/* Temporary checkbox */}
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={form.watch('isTemporary')}
                  onCheckedChange={(checked) => form.setValue('isTemporary', checked === true)}
                  disabled={isFormDisabled}
                />
                <span className="text-sm font-medium text-gray-700">Tabela provisória</span>
                <Tooltip.Provider delayDuration={200}>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <Info className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content
                        side="top"
                        align="start"
                        sideOffset={8}
                        className="z-50 max-w-sm rounded-md border bg-white px-3 py-2 text-xs text-gray-800 shadow-md"
                      >
                        Se acionado, habilita a tabela de forma temporária, ou seja, apenas ficará
                        em vigor até a data e hora de término informados no momento do cadastro.
                        Após terminado o período da tabela provisória, a última tabela não
                        provisória, volta a valer
                        <Tooltip.Arrow className="fill-white" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-900">
                    Data de início <span className="text-red-500">*</span>
                  </div>
                  <DateTimeInput
                    value={form.watch('startDateTime')}
                    onChange={(date) => {
                      form.setValue('startDateTime', date as Date);
                      form.clearErrors('startDateTime');
                    }}
                    placeholder="Selecione a data de início"
                    className="w-full"
                    disabled={isFormDisabled}
                    minDate={new Date()}
                    error={form.formState.errors.startDateTime?.message}
                  />
                </div>
                {form.watch('isTemporary') && (
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-gray-900">
                      Data de fim <span className="text-red-500">*</span>
                    </div>
                    <DateTimeInput
                      value={form.watch('endDateTime')}
                      onChange={(date) => {
                        form.setValue('endDateTime', date as Date);
                        form.clearErrors('endDateTime');
                      }}
                      placeholder="Selecione a data de fim"
                      className="w-full"
                      minDate={form.watch('startDateTime') || new Date()}
                      disabled={isFormDisabled}
                      error={form.formState.errors.endDateTime?.message}
                    />
                  </div>
                )}
              </div>

              {/* Billing Conditions */}
              <BillingConditionsSection
                ufList={[...BRAZILIAN_UF_LIST]}
                billToOptions={[...TO_BILL_FOR_OPTIONS]}
                onValidate={handleValidationConditions}
                disabled={isFormDisabled}
              />

              {/* Validation messages */}
              {messageErrorCondition.message.length > 0 &&
                messageErrorCondition.message.map((item) => (
                  <StatusBanner
                    key={item}
                    status={messageErrorCondition.status}
                    message={item}
                    statusStyles={STATUS_STYLES}
                  />
                ))}
            </div>
          </div>

          {/* Prices */}
          <div>
            <div className="border-b border-gray-200 w-full py-2">
              <p className="font-semibold text-gray-800 text-base">Preços</p>
            </div>
            <div className="pt-3 space-y-2">
              {/* Equipment with SIM Card */}
              <Collapsible defaultOpen={disclosureDefaultOpen.priceWithSim}>
                <CollapsibleTrigger className="border border-gray-200 rounded-md w-full px-3 py-2 flex justify-between items-center gap-2 bg-gray-50 group">
                  <h3 className="font-semibold text-sm">Venda com SIM Card</h3>
                  <ChevronDown className="w-4 h-4 text-gray-500 transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-1">
                  <div className="border border-gray-200 rounded-md p-2 space-y-1.5">
                    {loadingProducts ? (
                      <div className="text-center py-3">
                        <p className="text-sm text-gray-500">Carregando equipamentos...</p>
                      </div>
                    ) : equipmentModels.length === 0 ? (
                      <div className="text-center py-3">
                        <p className="text-sm text-gray-500">
                          Nenhum equipamento encontrado com categoria BWS_TRACKER
                        </p>
                      </div>
                    ) : (
                      equipmentModels.map((equipment, index) => (
                        <div key={`with-sim-${equipment.id}`}>
                          <div
                            className={`flex items-center justify-between px-3 py-2 rounded-md ${
                              index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                            }`}
                          >
                            <h4 className="font-semibold text-sm">{equipment.name}</h4>
                            <Switch
                              checked={enabledEquipmentWithSim[equipment.id] || false}
                              onCheckedChange={(enabled) =>
                                handleEquipmentToggle(equipment.id, enabled, 'withSim')
                              }
                              disabled={isFormDisabled}
                              title="Habilitar precificação"
                            />
                          </div>
                          {enabledEquipmentWithSim[equipment.id] && (
                            <div className="mt-2 mb-1">
                              <EquipmentPriceForm
                                equipmentModel={equipment.id}
                                disabled={isFormDisabled}
                                initialData={{
                                  creditPayment: existingEquipmentSimcardPayment?.find(
                                    (ep) =>
                                      ep.productId === equipment.id && ep.paymentType === 'credit',
                                  ),
                                  upfrontPayment: existingEquipmentSimcardPayment?.find(
                                    (ep) =>
                                      ep.productId === equipment.id && ep.paymentType === 'upfront',
                                  ),
                                }}
                                onPriceChange={(prices) => {
                                  handleEquipmentPriceChange(equipment.id, prices, 'withSim');
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Equipment without SIM Card */}
              <Collapsible defaultOpen={disclosureDefaultOpen.priceWithoutSim}>
                <CollapsibleTrigger className="border border-gray-200 rounded-md w-full px-3 py-2 flex justify-between items-center gap-2 bg-gray-50 group">
                  <h3 className="font-semibold text-sm">Venda sem SIM Card</h3>
                  <ChevronDown className="w-4 h-4 text-gray-500 transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-1">
                  <div className="border border-gray-200 rounded-md p-2 space-y-1.5">
                    {loadingProducts ? (
                      <div className="text-center py-3">
                        <p className="text-sm text-gray-500">Carregando equipamentos...</p>
                      </div>
                    ) : equipmentModels.length === 0 ? (
                      <div className="text-center py-3">
                        <p className="text-sm text-gray-500">
                          Nenhum equipamento encontrado com categoria BWS_TRACKER
                        </p>
                      </div>
                    ) : (
                      equipmentModels.map((equipment, index) => (
                        <div key={`without-sim-${equipment.id}`}>
                          <div
                            className={`flex items-center justify-between px-3 py-2 rounded-md ${
                              index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                            }`}
                          >
                            <h4 className="font-semibold text-sm">{equipment.name}</h4>
                            <Switch
                              checked={enabledEquipmentWithoutSim[equipment.id] || false}
                              onCheckedChange={(enabled) =>
                                handleEquipmentToggle(equipment.id, enabled, 'withoutSim')
                              }
                              disabled={isFormDisabled}
                              title="Habilitar precificação"
                            />
                          </div>
                          {enabledEquipmentWithoutSim[equipment.id] && (
                            <div className="mt-2 mb-1">
                              <EquipmentPriceForm
                                equipmentModel={equipment.id}
                                disabled={isFormDisabled}
                                initialData={{
                                  creditPayment: existingEquipmentPayment?.find(
                                    (ep) =>
                                      ep.productId === equipment.id && ep.paymentType === 'credit',
                                  ),
                                  upfrontPayment: existingEquipmentPayment?.find(
                                    (ep) =>
                                      ep.productId === equipment.id && ep.paymentType === 'upfront',
                                  ),
                                }}
                                onPriceChange={(prices) => {
                                  handleEquipmentPriceChange(equipment.id, prices, 'withoutSim');
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* SIM Card */}
              <Collapsible defaultOpen={disclosureDefaultOpen.simCard}>
                <CollapsibleTrigger className="border border-gray-200 rounded-md w-full px-3 py-2 flex justify-between items-center gap-2 bg-gray-50 group">
                  <h3 className="font-semibold text-sm">SIM Card</h3>
                  <ChevronDown className="w-4 h-4 text-gray-500 transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-1">
                  <SimCardPriceForm
                    initialData={existingSimcardPayment}
                    onPriceChange={handleSimCardPriceChange}
                    disabled={isFormDisabled}
                  />
                </CollapsibleContent>
              </Collapsible>

              {/* Accessories */}
              <Collapsible defaultOpen={disclosureDefaultOpen.accessories}>
                <CollapsibleTrigger className="border border-gray-200 rounded-md w-full px-3 py-2 flex justify-between items-center gap-2 bg-gray-50 group">
                  <h3 className="font-semibold text-sm">Venda de Acessórios</h3>
                  <ChevronDown className="w-4 h-4 text-gray-500 transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-1">
                  <div className="border border-gray-200 rounded-md p-2 space-y-1.5">
                    {loadingProducts ? (
                      <div className="text-center py-3">
                        <p className="text-sm text-gray-500">Carregando acessórios...</p>
                      </div>
                    ) : accessoriesItems.length === 0 ? (
                      <div className="text-center py-3">
                        <p className="text-sm text-gray-500">
                          Nenhum acessório encontrado com categoria BWS_ACCESSORY
                        </p>
                      </div>
                    ) : (
                      accessoriesItems.map((accessory, index) => (
                        <div key={`accessory-${accessory.id}`}>
                          <div
                            className={`flex items-center justify-between px-3 py-2 rounded-md ${
                              index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                            }`}
                          >
                            <h4 className="font-semibold text-sm">{accessory.name}</h4>
                            <Switch
                              checked={enabledAccessories[accessory.id] || false}
                              onCheckedChange={(enabled) =>
                                handleAccessoryToggle(accessory.id, enabled)
                              }
                              disabled={isFormDisabled}
                              title="Habilitar acessório"
                            />
                          </div>
                          {enabledAccessories[accessory.id] && (
                            <div className="mt-2 mb-1">
                              <EquipmentPriceForm
                                equipmentModel={accessory.id}
                                disabled={isFormDisabled}
                                initialData={{
                                  creditPayment: existingEquipmentPayment?.find(
                                    (ep) =>
                                      ep.productId === accessory.id && ep.paymentType === 'credit',
                                  ),
                                  upfrontPayment: existingEquipmentPayment?.find(
                                    (ep) =>
                                      ep.productId === accessory.id && ep.paymentType === 'upfront',
                                  ),
                                }}
                                onPriceChange={(prices) => {
                                  handleAccessoryPriceChange(accessory.id, prices);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Services */}
              <Collapsible defaultOpen={disclosureDefaultOpen.services}>
                <CollapsibleTrigger className="border border-gray-200 rounded-md w-full px-3 py-2 flex justify-between items-center gap-2 bg-gray-50 group">
                  <h3 className="font-semibold text-sm">Serviços</h3>
                  <ChevronDown className="w-4 h-4 text-gray-500 transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-1">
                  <ServicePriceForm
                    initialData={existingServicePayment}
                    onPriceChange={handleServicePriceChange}
                    disabled={isFormDisabled}
                  />
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>
        </form>
      </FormProvider>

      {/* Dialogs */}
      {priceTableId && (
        <>
          <PublishPriceTableDialog
            open={publishDialogOpen}
            onOpenChange={setPublishDialogOpen}
            priceTableId={priceTableId}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['price-table', priceTableId] });
              queryClient.invalidateQueries({ queryKey: ['price-tables'] });
            }}
            onValidationError={(errors) => {
              if (errors.startDateTime) {
                form.setError('startDateTime', { message: errors.startDateTime });
              }
              if (errors.endDateTime) {
                form.setError('endDateTime', { message: errors.endDateTime });
              }
            }}
          />
          <CancelPriceTableDialog
            open={cancelDialogOpen}
            onOpenChange={setCancelDialogOpen}
            priceTableId={priceTableId}
          />
          <InactivatePriceTableDialog
            open={inactivateDialogOpen}
            onOpenChange={setInactivateDialogOpen}
            priceTableId={priceTableId}
          />
        </>
      )}
    </div>
  );
}
